import { FormikErrors } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSnackbarStack } from '@superdispatch/ui';
import { FormikDrawerContent } from 'shared/form/FormikDrawer';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  verificationCodeSchema,
  VerificationCodeForm,
} from 'shared/form/VerificationCodeForm';
import {
  isInvalidCodeError,
  SendVerificationCodeMethod,
  useChangeBankAccount,
  useSendChangeBankAccountCode,
} from '../../data/ChangeBankAccountVerificationAPI';
import { useSuperPaySettings } from '../../data/SuperPayAPI';
import { isCodeRejectionError } from '../MoovError';
import { logPaymentError } from '../PaymentLogger';
import {
  BankAccountVerificationResult,
  ChangeBankAccountStepState,
  SubmittedBankAccount,
} from './ChangeBankAccountTypes';
import { ONE_MINUTE } from 'shared/constants/NumberConstants';
const INVALID_CODE_ERROR = 'Enter a valid verification code.';

const DEFAULT_VERIFICATION_RESULT: BankAccountVerificationResult = {
  status: 'verified',
  verificationMethod: 'instant',
};

interface VerificationFormValues {
  code: string;
}

interface SendVerificationCodeOptions {
  method: SendVerificationCodeMethod;
  successMessage: string;
}

interface UseChangeBankAccountConfirmFormProps {
  bankAccount: SubmittedBankAccount;
  onComplete: (result: BankAccountVerificationResult) => void;
}

export function useChangeBankAccountConfirmForm({
  bankAccount,
  onComplete,
}: UseChangeBankAccountConfirmFormProps) {
  const { addSnackbar } = useSnackbarStack();
  const [resendAvailableAt, setResendAvailableAt] = useState<
    DateTime | undefined
  >(getResendAvailableAt);
  const [verificationMethod, setVerificationMethod] =
    useState<SendVerificationCodeMethod>('email');

  const { mutateAsync, data: recipientData } = useSendChangeBankAccountCode();
  const { mutateAsync: changeBankAccount } = useChangeBankAccount();
  const { refetch: refetchSuperPaySettings } = useSuperPaySettings();

  const formik = useAppFormik<
    VerificationFormValues,
    BankAccountVerificationResult
  >({
    validationSchema: verificationCodeSchema,
    initialValues: { code: '' },
    onSubmit: async ({ code }) => {
      const response = await changeBankAccount({
        code,
        verificationMethod,
        holderName: bankAccount.holderName,
        routingNumber: bankAccount.routingNumber,
        accountNumber: bankAccount.accountNumber,
      });

      try {
        const { data } = await refetchSuperPaySettings();
        const [freshBankAccount] = data?.bank_accounts ?? [];
        return {
          ...response,
          data: {
            status:
              freshBankAccount?.verification_status ??
              DEFAULT_VERIFICATION_RESULT.status,
            verificationMethod:
              freshBankAccount?.verification_method ??
              DEFAULT_VERIFICATION_RESULT.verificationMethod,
          },
        };
      } catch (error) {
        logPaymentError(error as Error, 'ChangeBankAccountConfirmStep');
      }

      return { ...response, data: DEFAULT_VERIFICATION_RESULT };
    },
    onSubmitSuccess: (response) => {
      if (response.data.status === 'verified') {
        addSnackbar('Bank account updated', { variant: 'success' });
      }
      onComplete(response.data);
    },
    onSubmitFailure: (error: Error) => {
      if (isInvalidCodeError(error)) {
        setResendAvailableAt(undefined);
      }
    },
    getFormErrors: getVerificationFormErrors,
  });

  async function sendVerificationCode({
    method,
    successMessage,
  }: SendVerificationCodeOptions) {
    try {
      await mutateAsync(method);
      setVerificationMethod(method);
      setResendAvailableAt(getResendAvailableAt());
      formik.resetForm();
      addSnackbar(successMessage, { variant: 'success' });
    } catch (error) {
      addSnackbar((error as Error).message, { variant: 'error' });
    }
  }

  const sendCode = () => {
    void sendVerificationCode({
      method: verificationMethod,
      successMessage:
        verificationMethod === 'sms'
          ? 'Verification code sent via text message'
          : 'Verification code sent',
    });
  };

  useEffect(() => {
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    formik,
    resendAvailableAt,
    verificationMethod,
    recipient: recipientData?.data.recipient,
    sendCode,
    onSendEmail: () => {
      void sendVerificationCode({
        method: 'email',
        successMessage: 'Verification code sent',
      });
    },
    onSendTextMessage: () => {
      void sendVerificationCode({
        method: 'sms',
        successMessage: 'Verification code sent via text message',
      });
    },
  };
}

interface Props {
  bankAccount: SubmittedBankAccount;
  onClose: () => void;
  onComplete: (result: BankAccountVerificationResult) => void;
  onStepStateChange: (state: ChangeBankAccountStepState) => void;
}

export function ChangeBankAccountConfirmStep({
  bankAccount,
  onClose,
  onComplete,
  onStepStateChange,
}: Props) {
  const {
    formik,
    sendCode,
    resendAvailableAt,
    verificationMethod,
    recipient,
    onSendEmail,
    onSendTextMessage,
  } = useChangeBankAccountConfirmForm({ bankAccount, onComplete });

  const isCompleted = formik.status.type === 'submitted';

  useEffect(() => {
    onStepStateChange({
      isSubmitting: formik.isSubmitting,
      hasUnsavedChanges: !isCompleted,
    });
  }, [formik.isSubmitting, isCompleted, onStepStateChange]);

  return (
    <FormikDrawerContent
      formik={formik}
      title="Change Bank Account"
      onClose={onClose}
      disableNavigationBlock={true}
      disableAutoClose={true}
    >
      <VerificationCodeForm
        recipient={recipient}
        verificationMethod={verificationMethod}
        resendAvailableAt={resendAvailableAt}
        onResend={sendCode}
        onSendEmail={onSendEmail}
        onSendTextMessage={onSendTextMessage}
      />
    </FormikDrawerContent>
  );
}

function getResendAvailableAt() {
  return DateTime.local().plus({ milliseconds: ONE_MINUTE });
}

function getVerificationFormErrors(
  error: Error,
): FormikErrors<VerificationFormValues> {
  if (isInvalidCodeError(error)) {
    return { code: error.context?.code?.[0] ?? INVALID_CODE_ERROR };
  }

  if (isCodeRejectionError(error)) {
    return { code: INVALID_CODE_ERROR };
  }

  return {};
}
